import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Lock, Pencil, Save, X, Plus, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${window.location.origin}/api/budget`;
const DEFAULT_QUAL = "For budget/ROM purposes only. ABS is not liable for actual costs — contact ABS directly for competitive pricing if for award.";
const NEW_SECTION_SENTINEL = "__NEW_SECTION__";

const DIV_LABELS = {
  "div-08": "Division 08 — Openings",
  "div-10": "Division 10 — Specialties",
  "div-11": "Division 11 — Equipment & Furnishings",
  "div-12": "Division 12 — Furnishings",
  "div-14": "Division 14 — Conveying Equipment",
};

export default function AdminScopes() {
  const [scopes, setScopes] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const emptyCreate = {
    division_id: "div-10",
    section_id: "",
    new_section_code: "",
    new_section_name: "",
    code: "",
    name: "",
    description: "",
    qualification_text: DEFAULT_QUAL,
    vendor_notes: "",
  };
  const [createForm, setCreateForm] = useState(emptyCreate);

  // Duplicate modal state
  const [dupOpen, setDupOpen] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupSource, setDupSource] = useState(null);
  const [dupForm, setDupForm] = useState({ new_code: "", new_name: "" });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/scopes`),
      axios.get(`${API}/admin/sections`),
    ])
      .then(([s, sec]) => {
        setScopes(s.data);
        setDivisions(sec.data.divisions || []);
        setSections(sec.data.sections || []);
      })
      .catch(() => toast.error("Failed to load scopes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const startEdit = (scope) => {
    setEditId(scope.id);
    setEditData({
      name: scope.name,
      description: scope.description,
      vendor_notes: scope.vendor_notes,
      qualification_text: scope.qualification_text,
      is_active: scope.is_active,
    });
  };
  const cancelEdit = () => { setEditId(null); setEditData({}); };

  const saveEdit = async (scopeId) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/scopes/${scopeId}`, editData);
      setScopes(prev => prev.map(s => s.id === scopeId ? data : s));
      toast.success("Scope updated");
      cancelEdit();
    } catch {
      toast.error("Failed to update scope");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (scope) => {
    if (!scope.is_active && (scope.line_item_templates?.length || 0) === 0) {
      toast.warning("Add at least one line item in Pricing before activating.");
      return;
    }
    try {
      const { data } = await axios.put(`${API}/admin/scopes/${scope.id}`, { is_active: !scope.is_active });
      setScopes(prev => prev.map(s => s.id === scope.id ? data : s));
      toast.success(`Scope ${data.is_active ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Failed to toggle scope");
    }
  };

  const toggleSpecTracked = async (scope) => {
    try {
      const { data } = await axios.put(`${API}/admin/scopes/${scope.id}`, { spec_tracked: !scope.spec_tracked });
      setScopes(prev => prev.map(s => s.id === scope.id ? data : s));
      toast.success(`Scope ${data.spec_tracked ? "added to" : "removed from"} Basis of Design`);
    } catch {
      toast.error("Failed to toggle spec tracking");
    }
  };

  // --- Create ---
  const openCreate = () => {
    setCreateForm(emptyCreate);
    setCreateOpen(true);
  };

  const sectionsForDivision = sections.filter(s => s.division_id === createForm.division_id);

  const submitCreate = async () => {
    if (!createForm.name.trim()) return toast.error("Name is required");
    if (!/^\d{6}$/.test(createForm.code.trim())) return toast.error("Code must be a 6-digit CSI number");
    setCreateBusy(true);
    try {
      let sectionId = createForm.section_id;
      if (sectionId === NEW_SECTION_SENTINEL) {
        if (!/^\d{6}$/.test(createForm.new_section_code.trim())) {
          setCreateBusy(false); return toast.error("New section code must be a 6-digit CSI number");
        }
        if (!createForm.new_section_name.trim()) {
          setCreateBusy(false); return toast.error("New section name is required");
        }
        const { data: sec } = await axios.post(`${API}/admin/sections`, {
          division_id: createForm.division_id,
          code: createForm.new_section_code.trim(),
          name: createForm.new_section_name.trim(),
        });
        sectionId = sec.id;
        setSections(prev => [...prev, sec]);
      }
      if (!sectionId) { setCreateBusy(false); return toast.error("Pick a section"); }

      const { data } = await axios.post(`${API}/admin/scopes`, {
        section_id: sectionId,
        code: createForm.code.trim(),
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        qualification_text: createForm.qualification_text.trim(),
        vendor_notes: createForm.vendor_notes.trim(),
      });
      setScopes(prev => [...prev, data]);
      toast.success(`Created ${data.full_name}. Head to Pricing to add line items.`);
      setCreateOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create scope");
    } finally {
      setCreateBusy(false);
    }
  };

  // --- Duplicate ---
  const openDuplicate = (scope) => {
    setDupSource(scope);
    setDupForm({ new_code: "", new_name: `${scope.name} (Copy)` });
    setDupOpen(true);
  };

  const submitDuplicate = async () => {
    if (!dupSource) return;
    if (!/^\d{6}$/.test(dupForm.new_code.trim())) return toast.error("New code must be a 6-digit CSI number");
    if (!dupForm.new_name.trim()) return toast.error("New name is required");
    setDupBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/scopes/${dupSource.id}/duplicate`, {
        new_code: dupForm.new_code.trim(),
        new_name: dupForm.new_name.trim(),
      });
      setScopes(prev => [...prev, data]);
      toast.success(`Duplicated as ${data.full_name}`);
      setDupOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate");
    } finally {
      setDupBusy(false);
    }
  };

  // Group by division
  const grouped = scopes.reduce((acc, s) => {
    const div = s.division_id || "other";
    if (!acc[div]) acc[div] = [];
    acc[div].push(s);
    return acc;
  }, {});

  if (loading) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="p-4" data-testid="admin-scopes">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Scope Management
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Create, edit and activate scopes. Build pricing in the Pricing tab.</p>
        </div>
        <Button
          onClick={openCreate}
          size="sm" className="h-7 gap-1 px-2.5 text-xs"
          data-testid="new-scope-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Scope
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(grouped).map(([divId, divScopes]) => (
          <div key={divId} className="bg-white border border-slate-200 rounded-sm" data-testid={`scope-division-${divId}`}>
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-600">{DIV_LABELS[divId] || divId}</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {divScopes.map(scope => (
                <div key={scope.id} data-testid={`scope-row-${scope.id}`}>
                  {editId === scope.id ? (
                    <div className="p-5 bg-[#161616]/5 border-l-2 border-[#161616]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Display Name</Label>
                          <Input value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} className="rounded-sm h-8 text-sm" data-testid="edit-scope-name" />
                        </div>
                        <div className="flex items-center gap-3 pt-5">
                          <Switch checked={editData.is_active} onCheckedChange={v => setEditData(p => ({ ...p, is_active: v }))} data-testid="edit-scope-active" />
                          <span className="text-sm text-slate-600">{editData.is_active ? "Active" : "Inactive (placeholder)"}</span>
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
                          <Textarea value={editData.description} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))} className="rounded-sm text-sm min-h-[60px]" data-testid="edit-scope-description" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Vendor Notes</Label>
                          <Input value={editData.vendor_notes} onChange={e => setEditData(p => ({ ...p, vendor_notes: e.target.value }))} className="rounded-sm h-8 text-sm" data-testid="edit-scope-vendor" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Qualification Text</Label>
                          <Textarea value={editData.qualification_text} onChange={e => setEditData(p => ({ ...p, qualification_text: e.target.value }))} className="rounded-sm text-sm min-h-[60px]" data-testid="edit-scope-qualification" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(scope.id)} disabled={saving} className="rounded-sm text-xs gap-1" data-testid="save-scope-btn">
                          <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-sm text-xs gap-1 border-slate-300" data-testid="cancel-scope-btn">
                          <X className="w-3 h-3" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50/50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800">{scope.full_name || scope.name}</span>
                          {scope.is_active ? (
                            <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] rounded-sm">Active</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px] rounded-sm">Placeholder</Badge>
                          )}
                          {scope.source === "user" && (
                            <Badge className="bg-muted text-foreground border-border text-[10px] rounded-sm">Custom</Badge>
                          )}
                          {scope.line_item_templates?.length > 0 && (
                            <span className="text-[10px] text-slate-400">{scope.line_item_templates.length} items</span>
                          )}
                        </div>
                        {scope.description && <p className="text-xs text-slate-400 mt-0.5">{scope.description}</p>}
                        {scope.vendor_notes && <p className="text-xs text-[#161616] mt-0.5">{scope.vendor_notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <label className="flex items-center gap-1 text-[11px] text-slate-500 mr-2 cursor-pointer" title="Show this scope on the Basis of Design dashboard">
                          <input
                            type="checkbox"
                            checked={!!scope.spec_tracked}
                            onChange={() => toggleSpecTracked(scope)}
                            className="w-3.5 h-3.5 accent-[#161616]"
                            data-testid={`spec-tracked-${scope.id}`}
                          />
                          Spec
                        </label>
                        <Switch
                          checked={scope.is_active}
                          onCheckedChange={() => toggleActive(scope)}
                          data-testid={`toggle-scope-${scope.id}`}
                        />
                        <button onClick={() => openDuplicate(scope)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" title="Duplicate as new scope" data-testid={`duplicate-scope-${scope.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => startEdit(scope)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" title="Edit" data-testid={`edit-scope-${scope.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create Scope Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="new-scope-modal">
          <DialogHeader>
            <DialogTitle className="text-base">New Scope</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create the scope shell. Line items and prices are managed in the Pricing tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Division</Label>
                <Select
                  value={createForm.division_id}
                  onValueChange={v => setCreateForm(p => ({ ...p, division_id: v, section_id: "" }))}
                >
                  <SelectTrigger className="h-9 text-sm rounded-sm border-slate-200" data-testid="new-scope-division">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {divisions.map(d => <SelectItem key={d.id} value={d.id}>{DIV_LABELS[d.id] || d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Section</Label>
                <Select
                  value={createForm.section_id}
                  onValueChange={v => setCreateForm(p => ({ ...p, section_id: v }))}
                >
                  <SelectTrigger className="h-9 text-sm rounded-sm border-slate-200" data-testid="new-scope-section">
                    <SelectValue placeholder="Pick a section…" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectionsForDivision.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
                    ))}
                    <SelectItem value={NEW_SECTION_SENTINEL}>+ New section…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {createForm.section_id === NEW_SECTION_SENTINEL && (
              <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Section CSI Code (6 digits)</Label>
                  <Input
                    value={createForm.new_section_code}
                    onChange={e => setCreateForm(p => ({ ...p, new_section_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    placeholder="101400"
                    className="h-9 text-sm rounded-sm border-slate-200 font-mono"
                    data-testid="new-scope-new-section-code"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500 mb-1 block">Section Name</Label>
                  <Input
                    value={createForm.new_section_name}
                    onChange={e => setCreateForm(p => ({ ...p, new_section_name: e.target.value }))}
                    placeholder="e.g. Signage"
                    className="h-9 text-sm rounded-sm border-slate-200"
                    data-testid="new-scope-new-section-name"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Scope CSI Code <span className="text-red-500">*</span></Label>
                <Input
                  value={createForm.code}
                  onChange={e => setCreateForm(p => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="103300"
                  className="h-9 text-sm rounded-sm border-slate-200 font-mono"
                  data-testid="new-scope-code"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Scope Name <span className="text-red-500">*</span></Label>
                <Input
                  value={createForm.name}
                  onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Signage"
                  className="h-9 text-sm rounded-sm border-slate-200"
                  data-testid="new-scope-name"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
              <Textarea
                value={createForm.description}
                onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Short description (optional)"
                className="text-sm rounded-sm border-slate-200 min-h-[60px]"
                data-testid="new-scope-description"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Vendor Notes</Label>
              <Input
                value={createForm.vendor_notes}
                onChange={e => setCreateForm(p => ({ ...p, vendor_notes: e.target.value }))}
                placeholder="e.g. Preferred vendor / lead time notes"
                className="h-9 text-sm rounded-sm border-slate-200"
                data-testid="new-scope-vendor"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Qualification Text</Label>
              <Textarea
                value={createForm.qualification_text}
                onChange={e => setCreateForm(p => ({ ...p, qualification_text: e.target.value }))}
                className="text-sm rounded-sm border-slate-200 min-h-[70px]"
                data-testid="new-scope-qualification"
              />
            </div>

            <div className="text-xs bg-muted border border-border rounded-sm p-3 text-foreground">
              <div className="font-medium mb-1">What happens next:</div>
              4 default pricing modifiers (Contingency 10%, Site Logistics 5%, Travel 0%, Other 0%) will be added automatically.
              The scope starts as <strong>Inactive</strong> — head to <strong>Pricing</strong> to add line items, then flip the switch here to make it available in New Budget.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} className="rounded-sm border-slate-300 text-slate-600 text-xs" data-testid="new-scope-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" onClick={submitCreate} disabled={createBusy} className="rounded-sm text-xs" data-testid="new-scope-submit-btn">
              {createBusy ? "Creating…" : "Create Scope"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Modal */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-md" data-testid="duplicate-scope-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Duplicate Scope</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Clone <span className="font-medium text-slate-700">{dupSource?.full_name || dupSource?.name}</span> including all line items and modifier defaults. New scope starts inactive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">New CSI Code (6 digits) <span className="text-red-500">*</span></Label>
              <Input
                value={dupForm.new_code}
                onChange={e => setDupForm(p => ({ ...p, new_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="e.g. 103301"
                className="h-9 text-sm rounded-sm border-slate-200 font-mono"
                data-testid="duplicate-scope-code"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">New Name <span className="text-red-500">*</span></Label>
              <Input
                value={dupForm.new_name}
                onChange={e => setDupForm(p => ({ ...p, new_name: e.target.value }))}
                className="h-9 text-sm rounded-sm border-slate-200"
                data-testid="duplicate-scope-name"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDupOpen(false)} className="rounded-sm border-slate-300 text-slate-600 text-xs" data-testid="duplicate-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" onClick={submitDuplicate} disabled={dupBusy} className="rounded-sm text-xs" data-testid="duplicate-submit-btn">
              {dupBusy ? "Duplicating…" : "Duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
